import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RouteNames } from "../../navigation/RouteNames";
import { useAppSelector } from "../../store/hooks";
import { useTranslation } from "../../translations/i18n";
import { formatLongDisplayDate } from "../../utils/helperFunctions/dateFormatter";
import { canManageBilling } from "../../utils/helperFunctions/roleHelper";
import {
  getSubscriptionAccessState,
  getSubscriptionExpiryValue,
  isCustomSubscription,
} from "../../utils/helperFunctions/subscriptionAccessHelper";
import AppButton from "../button/AppButton";

const SubscriptionAccessBanner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { account, user } = useAppSelector((state) => state.auth);

  const accessState = getSubscriptionAccessState(account);

  if (!account || accessState === "active") {
    return null;
  }

  const customSubscription = isCustomSubscription(account);

  const canTakeBillingAction = canManageBilling(user?.role);

  const expiryValue = getSubscriptionExpiryValue(account);

  const formattedExpiryDate = expiryValue
    ? formatLongDisplayDate(expiryValue)
    : "-";

  const title =
    accessState === "expired"
      ? t("subscription.accessExpiredTitle")
      : t("subscription.accessWarningTitle");

  const message =
    accessState === "expired"
      ? t(
          customSubscription
            ? "subscription.customAccessExpiredMessage"
            : "subscription.accessExpiredMessage",
        )
      : t(
          customSubscription
            ? "subscription.customAccessWarningMessage"
            : "subscription.accessWarningMessage",
          {
            date: formattedExpiryDate,
          },
        );

  const handleAction = () => {
    navigate(customSubscription ? RouteNames.support : RouteNames.subscription);
  };

  return (
    <Alert
      severity="error"
      variant="filled"
      sx={{
        mb: 3,
        px: {
          xs: 2,
          sm: 3,
        },
        py: 1.5,
        borderRadius: 3,
        alignItems: "flex-start",
        boxShadow: 3,
        "& .MuiAlert-message": {
          width: "100%",
        },
      }}
      action={
        canTakeBillingAction ? (
          <AppButton
            variant="outlined"
            color="error"
            onClick={handleAction}
            title={t(
              customSubscription
                ? "subscription.contactSupportCta"
                : "subscription.viewPlansCta",
            )}
          />
        ) : undefined
      }
    >
      <AlertTitle
        sx={{
          mb: 0.75,
          fontSize: {
            xs: 18,
            sm: 21,
          },
          fontWeight: 950,
        }}
      >
        {title}
      </AlertTitle>

      <Box>
        <Typography
          sx={{
            fontSize: {
              xs: 15,
              sm: 17,
            },
            fontWeight: 750,
            lineHeight: 1.5,
          }}
        >
          {message}
        </Typography>

        {!canTakeBillingAction ? (
          <Typography
            sx={{
              mt: 0.75,
              fontWeight: 800,
            }}
          >
            {t("subscription.contactAccountOwnerMessage")}
          </Typography>
        ) : null}
      </Box>
    </Alert>
  );
};

export default SubscriptionAccessBanner;
